import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import {
  ORDER_ISSUE_TYPES,
  orderIssueSchema,
  orderIssueResultSchema,
} from "../accounts-receivable/sales-order-drafts/detect-order-issues.step";

/**
 * Notification types for order-related emails.
 * Each type produces a different email template.
 */
export const ORDER_NOTIFICATION_TYPES = [
  "issues_detected", // Order has issues requiring review
  "order_submitted", // Order was auto-submitted successfully
  "order_ready_for_review", // Order is ready for manual review (no auto-submit)
] as const;

export type OrderNotificationType = (typeof ORDER_NOTIFICATION_TYPES)[number];

/**
 * Render Order Notification Email Step
 *
 * Renders email content for order-related notifications based on issue detection
 * results and notification type. This step isolates email template logic from
 * the upstream detection and downstream sending steps.
 *
 * Supports multiple notification scenarios:
 * - issues_detected: Notify watchers about orders needing review
 * - order_submitted: Confirm auto-submitted orders
 * - order_ready_for_review: Notify about orders awaiting manual review
 */
export const renderOrderNotificationEmailStep = defineStepConfig({
  type: "render_order_notification_email",
  name: "Render Order Notification Email",
  description:
    "Renders email subject and HTML content for order-related notifications",
  category: "Transformation",
  deprecated: {
    reason:
      "Legacy order-notification flow. OMS v3 sends approval/notification emails from the Approval step + NotificationService; this hardcoded order-notification email renderer is no longer used.",
  },
  inputSchema: z.object({
    orderResults: z
      .array(orderIssueResultSchema)
      .optional()
      .describe(
        "Order issue results from detect-order-issues step. When omitted, provide `salesOrderId` and the handler will hydrate this from the DB.",
      ),
    salesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Sales order to render a notification for. Used as a fallback when `orderResults` is not wired — the handler loads the underlying staged PO and builds a single-order result.",
      ),
    stagedPurchaseOrderIds: z
      .array(z.string().uuid())
      .optional()
      .describe(
        "Order IDs to include in the notification (optional, defaults to all from orderResults)",
      ),
    customerName: z
      .string()
      .nullable()
      .optional()
      .describe("Customer name for the email (optional, derived from results)"),
    watcherRole: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Watcher role this notification is addressed to (e.g., 'Remittance / Finance'). When provided, it replaces the generic 'Order' / 'Discount' wording in the email subject so recipients see who is expected to act.",
      ),
    oracleOrderData: z
      .array(
        z.object({
          stagedPurchaseOrderId: z.string().uuid(),
          oracleOrderNumber: z.string().nullable(),
          origSysDocRef: z.string().nullable(),
        }),
      )
      .optional()
      .describe(
        "Oracle order data for displaying Oracle Order # in emails, keyed by stagedPurchaseOrderId",
      ),
    idempotencyKeyPrefixOverride: z
      .string()
      .optional()
      .describe(
        "Optional override for the email's dedupe-key prefix. When omitted, the handler computes one from the notification type and the order id. Wire this only when an upstream step (e.g. detect-order-issues) needs a specific prefix shape — most workflows should leave it unwired.",
      ),
  }),

  outputSchema: z.object({
    skipped: z
      .boolean()
      .default(false)
      .describe("Whether the step was skipped (e.g., no orders to render)"),
    skippedReason: z
      .string()
      .optional()
      .describe("Reason for skipping, if skipped is true"),
    subject: z.string().describe("The rendered email subject line"),
    htmlContent: z.string().describe("The rendered HTML email body"),
    orderCount: z.number().describe("Number of orders included in the email"),
    issueCount: z.number().describe("Total number of issues across all orders"),
    idempotencyKeyPrefix: z
      .string()
      .nullable()
      .describe(
        "Dedupe-key prefix to forward to send_email. Falls through from `idempotencyKeyPrefixOverride` if provided, otherwise computed as `${notificationType}:${salesOrderId|stagedPoId}` (with `:role:${watcherRole}` appended when a watcher role is set). Null when the step is skipped.",
      ),
  }),

  configSchema: z.object({
    notificationType: z
      .enum(ORDER_NOTIFICATION_TYPES)
      .describe("Type of notification to render"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});

// Re-export types for handler use (orderSummarySchema not re-exported to avoid collision)
export { ORDER_ISSUE_TYPES, orderIssueSchema };
